import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { staffApi } from "@/lib/staffApi";
import { formatMoney } from "@/lib/format";

export function PaymentPanel({ appointmentId, appt, onSynced, visit }) {
    const [data, setData] = useState(null);
    const [target, setTarget] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [watchUntil, setWatchUntil] = useState(0);
    const [retryRequest, setRetryRequest] = useState(null);
    const isVisit = !!visit;
    const visitVersion = visit?.version;
    const load = useCallback(async () => {
        try {
            const value = isVisit ? await staffApi.getVisit(appointmentId) : null;
            const next = value ? {deposit_required:value.amounts.deposit_target_pence/100, standard_deposit:value.amounts.standard_deposit_pence/100,
                service_total:value.amounts.total_pence/100, amount_paid_total:value.amounts.net_paid_pence/100,
                remaining_balance:value.amounts.balance_due_pence/100, version:value.version} : await staffApi.getAppointmentPayment(appointmentId);
            setData(next); setTarget(Number(next.deposit_required || 0).toFixed(2)); setError("");
        } catch (e) { setError(e.message || "Could not load deposit details."); }
    }, [appointmentId, isVisit, visitVersion]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!watchUntil || visit) return;
        let active = true, pending = false;
        const timer = setInterval(async () => {
            if (Date.now() >= watchUntil) { setWatchUntil(0); return; }
            if (pending) return;
            pending = true;
            try {
                const next = await staffApi.syncAppointmentPayment(appointmentId);
                if (active && next) {
                    setData(next);
                    if (Number(next.amount_paid_total || 0) >= Number(next.deposit_required || 0)) {
                        setWatchUntil(0); if (onSynced) onSynced();
                    }
                }
            } catch (_) { /* Manual refresh displays any persistent error. */ }
            finally { pending = false; }
        }, 10000);
        return () => { active = false; clearInterval(timer); };
    }, [watchUntil, appointmentId, onSynced, visit]);
    const paid = Number(data?.amount_paid_total || 0);
    const standard = Number(data?.standard_deposit ?? data?.deposit_required ?? 0);
    const total = Number(data?.service_total || 0);
    const amount = Number(target);
    const due = Math.max(Math.round((amount - paid) * 100) / 100, 0);
    const valid = /^\d+(\.\d{1,2})?$/.test(target) && amount >= standard && amount <= total && due > 0;
    let phone = String(appt?.customer?.phone || "").replace(/[^0-9]/g, "");
    if (phone.startsWith("00")) phone = phone.slice(2);
    if (phone.startsWith("0")) phone = "44" + phone.slice(1);
    const send = async (channel) => {
        if (busy || (!valid && !retryRequest) || (retryRequest && retryRequest.channel !== channel)) return;
        setBusy(true); setError("");
        const tab = channel === "whatsapp" ? window.open("", "_blank") : null;
        if (tab) tab.opener = null;
        try {
            if (visit) {
                const request = retryRequest || {request_id:crypto.randomUUID(),version:data.version,deposit_target:Number(target),channel};
                setRetryRequest(request);
                const result = await staffApi.sendVisitDeposit(appointmentId, request);
                if (channel === 'email') toast.success('Deposit email queued');
                else {
                    if (!tab) throw new Error('Allow this page to open WhatsApp, then try again.');
                    const link = result.payment_link;
                    if (!link?.url || !Number.isFinite(Number(link.amount_pence))) throw new Error('Deposit link could not be verified.');
                    const text = `Hi ${appt?.customer?.firstName || 'there'}, please pay ${formatMoney(link.amount_pence/100)} towards your appointment deposit using this secure link: ${link.url}. Thank you, Pout Aesthetics.`;
                    tab.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
                    toast.success('WhatsApp opened. Review the message and press Send.');
                }
                setRetryRequest(null); await load(); onSynced?.(); return;
            }
            const payload = { deposit_target: target, purpose: "deposit" };
            if (channel === "email") {
                let result = await staffApi.sendPaymentLinkEmail(appointmentId, payload);
                if (result.reason === "already_sent" && window.confirm("A deposit email has already been sent. Send another?")) {
                    result = await staffApi.sendPaymentLinkEmail(appointmentId, { ...payload, force: true });
                }
                if (result.sent) toast.success("Deposit email queued");
            } else {
                if (!tab) throw new Error("Allow this page to open WhatsApp, then try again.");
                const result = await staffApi.generatePaymentLink(appointmentId, payload);
                const link = result.payment_link;
                if (!link?.url || link.purpose !== "deposit" || !Number.isFinite(Number(link.amount_due))) throw new Error("Deposit link could not be verified.");
                const text = `Hi ${appt?.customer?.firstName || "there"}, please pay ${formatMoney(Number(link.amount_due))} towards your appointment deposit using this secure link: ${link.url}. Thank you, Pout Aesthetics.`;
                tab.location.href = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
                toast.success("WhatsApp opened. Review the message and press Send.");
            }
            await load();
            setWatchUntil(Date.now() + 5 * 60 * 1000);
            if (onSynced) onSynced();
        } catch (e) {
            if (tab) tab.close();
            if ([400, 401, 403, 404, 409, 422].includes(e.status)) { setRetryRequest(null); await load(); }
            setError(e.message || "Could not prepare the deposit request.");
        }
        finally { setBusy(false); }
    };
    const refresh = async () => {
        setBusy(true);
        try { if (!visit) await staffApi.syncAppointmentPayment(appointmentId); await load(); if (onSynced) onSynced(); }
        catch (e) { setError(e.message || "Could not refresh payment."); }
        finally { setBusy(false); }
    };
    return <section className="min-w-0 w-full rounded-2xl glass p-5 space-y-5" data-testid="deposit-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-2xl text-white">Deposit</h2>
            <button onClick={refresh} disabled={busy} className="text-sm text-gold">Refresh</button>
        </div>
        {error && <p role="alert" className="text-sm text-amber-200">{error}</p>}
        {!data ? <p className="text-white/70">Loading deposit details…</p> : <>
            <div className="grid grid-cols-2 gap-4 text-white break-words">
                <div><p className="text-sm text-white/60">Treatment price</p><p>{formatMoney(total)}</p></div>
                <div><p className="text-sm text-white/60">Standard deposit</p><p>{formatMoney(standard)}</p></div>
                <div><p className="text-sm text-white/60">Amount paid</p><p>{formatMoney(paid)}</p></div>
                <div><p className="text-sm text-white/60">Outstanding balance</p><p>{formatMoney(Number(data.remaining_balance || 0))}</p></div>
            </div>
            <label className="block text-white text-sm">Total deposit requested (£)
                <input aria-label="Total deposit requested" type="text" inputMode="decimal" value={target} disabled={busy || !!retryRequest}
                    onChange={e => setTarget(e.target.value)} className="mt-2 w-full min-w-0 rounded-xl bg-white text-slate-900 px-4 py-3" />
            </label>
            <p className="text-xs text-white/70">Enter the standard deposit or a higher agreed amount, up to the treatment price. Payments already received are deducted. This amount is saved when you prepare a request; it does not mark money as paid.</p>
            <p className="text-gold">{valid ? `Request now: ${formatMoney(due)}` : amount <= paid && target !== "" ? "Deposit covered. Collect the remaining balance through appointment checkout." : "Enter a valid deposit amount within the limits above."}</p>
            <div className="flex flex-wrap gap-3">
                <button disabled={busy || (!valid && !retryRequest) || (retryRequest && retryRequest.channel !== 'email') || !appt?.customer?.email} onClick={() => send("email")} className="rounded-full bg-gold text-navy px-5 py-3 font-semibold disabled:opacity-40">{retryRequest?.channel === 'email' ? 'Retry Deposit Email' : 'Send Deposit Email'}</button>
                <button disabled={busy || (!valid && !retryRequest) || (retryRequest && retryRequest.channel !== 'whatsapp') || phone.length < 8} onClick={() => send("whatsapp")} className="rounded-full border border-gold text-gold px-5 py-3 font-semibold disabled:opacity-40">{retryRequest?.channel === 'whatsapp' ? 'Retry Deposit WhatsApp' : 'Send Deposit WhatsApp'}</button>
            </div>
            {watchUntil > 0 && <p className="text-xs text-white/60">Checking for payment every 10 seconds.</p>}
        </>}
    </section>;
}
